import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format, isValid as isValidDate } from 'date-fns';
import {
	DayOfWeekEnum,
	ProviderPaymentDateRuleDTO,
	ProviderPaymentDateRuleUpsertDTO,
	StrategyCode,
	TriggerSource,
} from '../../types';
import { findPaymentDateRule, upsertPaymentDateRule } from '../../services';
import { allowNegativeNetDays, isIntAny, validateUpsert } from './providerPaymentDateRule.validation';
import { defaultAlertState } from '../../../common/constants';
import { extractErrorMessage } from '../../../quotation/helpers';
import { ShowAlertState } from '../../../common/types';

export type ProviderPaymentDateRuleModalProps = {
	open: boolean;
	providerId: string;
	providerFantasyName: string;
	onClose: () => void;
	onSaved?: () => void;
};

type Option<T extends string> = { value: T; label: string };

const STRATEGY_LABEL: Record<StrategyCode, string> = {
	[StrategyCode.NetDays]: 'Días netos (NetDays)',
	[StrategyCode.SplitMonth]: 'Corte mensual (SplitMonth)',
	[StrategyCode.MonthlyRelative]: 'Mensual relativo (MonthlyRelative)',
	[StrategyCode.WeeklyRelative]: 'Semanal relativo (WeeklyRelative)',
	[StrategyCode.CustomCalendar]: 'Calendario personalizado (CustomCalendar)',
};

const TRIGGER_LABEL: Record<TriggerSource, string> = {
	[TriggerSource.ServiceCreation]: 'Creación del servicio (ServiceCreation)',
	[TriggerSource.CheckIn]: 'Inicio del servicio (CheckIn)',
	[TriggerSource.CheckOut]: 'Fin del servicio (CheckOut)',
};

const DOW_LABEL: Record<DayOfWeekEnum, string> = {
	[DayOfWeekEnum.Monday]: 'Lunes',
	[DayOfWeekEnum.Tuesday]: 'Martes',
	[DayOfWeekEnum.Wednesday]: 'Miércoles',
	[DayOfWeekEnum.Thursday]: 'Jueves',
	[DayOfWeekEnum.Friday]: 'Viernes',
	[DayOfWeekEnum.Saturday]: 'Sábado',
	[DayOfWeekEnum.Sunday]: 'Domingo',
};

const PROVIDER_TRIGGER_SOURCES: TriggerSource[] = [
	TriggerSource.ServiceCreation,
	TriggerSource.CheckIn,
	TriggerSource.CheckOut,
];

const STRATEGY_OPTIONS: Option<StrategyCode>[] = (Object.values(StrategyCode) as StrategyCode[]).map((v) => ({
	value: v,
	label: STRATEGY_LABEL[v] ?? v,
}));

const TRIGGER_OPTIONS: Option<TriggerSource>[] = PROVIDER_TRIGGER_SOURCES.map((v) => ({
	value: v,
	label: TRIGGER_LABEL[v] ?? v,
}));

const DOW_OPTIONS: Option<DayOfWeekEnum>[] = (Object.values(DayOfWeekEnum) as DayOfWeekEnum[]).map((v) => ({
	value: v,
	label: DOW_LABEL[v] ?? v,
}));

function ymdToLocalDate(value?: string | null): Date | null {
	if (!value) return null;
	const [yy, mm, dd] = value.split('-').map(Number);
	if (!yy || !mm || !dd) return null;

	// Local + al mediodía para evitar corrimientos por TZ/DST
	return new Date(yy, mm - 1, dd, 12, 0, 0, 0);
}

function localDateToYmd(date: Date | null): string {
	if (!date) return '';
	if (!isValidDate(date)) return '';
	const d = new Date(date);
	d.setHours(12, 0, 0, 0);
	return format(d, 'yyyy-MM-dd');
}

function getErrorMessage<T>(errors: FieldErrors<T>, path: string): string | undefined {
	const parts = path.split('.');
	let cur: any = errors;
	for (const p of parts) {
		if (cur == null) return undefined;
		cur = cur[p];
	}
	return cur?.message as string | undefined;
}

function hasError<T>(errors: FieldErrors<T>, path: string): boolean {
	return Boolean(getErrorMessage(errors, path));
}

function isIntGte0(v: unknown) {
	if (v === '' || v === null || v === undefined) return false;
	const n = Number(v);
	return Number.isInteger(n) && n >= 0;
}

function isIntBetween(v: unknown, min: number, max: number) {
	if (v === '' || v === null || v === undefined) return false;
	const n = Number(v);
	return Number.isInteger(n) && n >= min && n <= max;
}

function defaultForm(): ProviderPaymentDateRuleUpsertDTO {
	// SOLO defaults: estrategia + fecha base
	return {
		strategyCode: StrategyCode.NetDays,
		configuration: {
			triggerSource: TriggerSource.ServiceCreation,
		} as any,
	};
}

/**
 * Cuando el usuario cambia estrategia: armamos estructura mínima SIN defaults numéricos.
 * (y para SplitMonth garantizamos que existan firstPeriod/secondPeriod como objetos)
 */
function buildEmptyConfig(strategyCode: StrategyCode, triggerSource: TriggerSource): ProviderPaymentDateRuleUpsertDTO {
	if (strategyCode === StrategyCode.NetDays) {
		return { strategyCode, configuration: { triggerSource } as any };
	}

	if (strategyCode === StrategyCode.WeeklyRelative) {
		return { strategyCode, configuration: { triggerSource } as any };
	}

	if (strategyCode === StrategyCode.MonthlyRelative) {
		return { strategyCode, configuration: { triggerSource } as any };
	}

	if (strategyCode === StrategyCode.SplitMonth) {
		return {
			strategyCode,
			configuration: {
				triggerSource,
				firstPeriod: {} as any,
				secondPeriod: {} as any,
			} as any,
		};
	}

	// CustomCalendar (periods se sincroniza con useFieldArray.replace)
	return {
		strategyCode,
		configuration: {
			triggerSource,
		} as any,
	};
}

const ProviderPaymentDateRuleModal: React.FC<ProviderPaymentDateRuleModalProps> = ({
	open,
	providerId,
	providerFantasyName,
	onClose,
	onSaved,
}: ProviderPaymentDateRuleModalProps) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const {
		unregister,
		trigger,
		control,
		handleSubmit,
		reset,
		watch,
		setError,
		clearErrors,
		getValues,
		setValue,
		formState: { errors, isValid },
	} = useForm<ProviderPaymentDateRuleUpsertDTO>({
		defaultValues: defaultForm(),
		mode: 'onChange',
		reValidateMode: 'onChange',
		shouldUnregister: false, // CLAVE: no perder valores cuando cambia el render condicional
	});

	const strategyCode = watch('strategyCode') as StrategyCode;

	const periodsArray = useFieldArray({
		control,
		name: 'configuration.periods' as any,
	});

	const fallbackStrategyCode = watch('configuration.fallbackStrategy.strategyCode' as any) as StrategyCode | undefined;

	// Cargar datos al abrir
	useEffect(() => {
		if (!open) return;

		let cancelled = false;
		setLoading(true);

		(async () => {
			try {
				const resp = await findPaymentDateRule(providerId);

				if (resp.status === 404) {
					if (!cancelled) {
						reset(defaultForm());
						periodsArray.replace([]);
					}
					return;
				}

				const data: ProviderPaymentDateRuleDTO = resp.data;

				if (!cancelled) {
					reset(data as any);

					// sincronizar periods con useFieldArray
					if ((data as any)?.strategyCode === StrategyCode.CustomCalendar) {
						const incoming = ((data as any)?.configuration?.periods ?? []) as any[];
						periodsArray.replace(incoming.length > 0 ? incoming : [{ from: null, to: null, dueDate: null }]);
					} else {
						periodsArray.replace([]);
					}
				}
			} catch {
				if (!cancelled) {
					reset(defaultForm());
					periodsArray.replace([]);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, providerId, reset]);

	const triggerSource = useWatch({
		control,
		name: 'configuration.triggerSource' as any,
	}) as TriggerSource | undefined;

	useEffect(() => {
		// Cuando cambia triggerSource, revalidar fields dependientes
		// (si no, el error queda "pegado" hasta tocar el input)
		clearErrors(['configuration.days' as any, 'configuration.fallbackStrategy.days' as any] as any);

		// Dispara revalidación (no hace falta await)
		trigger(['configuration.days' as any, 'configuration.fallbackStrategy.days' as any] as any);
	}, [triggerSource, clearErrors, trigger]);

	const onSubmit = async (values: ProviderPaymentDateRuleUpsertDTO) => {
		clearErrors();

		if (!PROVIDER_TRIGGER_SOURCES.includes(values.configuration.triggerSource as any)) {
			setError('configuration.triggerSource' as any, {
				type: 'manual',
				message: 'Trigger source inválido para reglas de proveedor.',
			});
			return;
		}

		// CustomCalendar debe tener al menos 1 período
		if (values.strategyCode === StrategyCode.CustomCalendar) {
			const periods = (values as any)?.configuration?.periods as any[] | undefined;
			if (!periods || periods.length < 1) {
				setError('configuration.periods' as any, {
					type: 'manual',
					message: 'Debés configurar al menos un período.',
				});
				return;
			}
		}

		const validationErrors = validateUpsert(values);
		const keys = Object.keys(validationErrors);
		if (keys.length > 0) {
			keys.forEach((k) => setError(k as any, { type: 'manual', message: (validationErrors as any)[k] }));
			return;
		}

		setSaving(true);
		try {
			await upsertPaymentDateRule(providerId, values);
			setAlert({
				show: true,
				severity: 'success',
				message: 'Regla de fecha de pago guardada satisfactoriamente',
			});

			setTimeout(() => {
				onSaved?.();
				onClose();
			}, 1000);
		} catch (e: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(e, 'Ocurrió un error al guardar la configuración'),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleCloseAlert = () => {
		setAlert(defaultAlertState);
	};

	const disableClose = saving;

	return (
		<>
			<Dialog
				open={open}
				onClose={(_, reason) => {
					if (disableClose) return;
					// bloquear click afuera y Escape
					if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
					onClose();
				}}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>Regla de fecha de pago del proveedor {providerFantasyName}</DialogTitle>

				<DialogContent>
					{loading ? (
						<Box display="flex" alignItems="center" justifyContent="center" py={6}>
							<CircularProgress />
						</Box>
					) : (
						<Box display="flex" flexDirection="column" gap={2} mt={1}>
							<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
								<Controller
									name="strategyCode"
									control={control}
									rules={{ required: 'Estrategia es requerida' }}
									render={({ field }) => (
										<FormControl fullWidth size="small" error={hasError(errors, 'strategyCode')}>
											<InputLabel>Estrategia</InputLabel>
											<Select
												{...field}
												label="Estrategia"
												value={field.value ?? StrategyCode.NetDays}
												onChange={(e) => {
													const next = e.target.value as StrategyCode;
													const prev = getValues('strategyCode') as StrategyCode;

													field.onChange(next);

													// si estoy SALIENDO de CustomCalendar → borro periods
													if (prev === StrategyCode.CustomCalendar && next !== StrategyCode.CustomCalendar) {
														periodsArray.replace([]); // UI
														setValue('configuration.periods' as any, [], {
															shouldDirty: true,
															shouldTouch: true,
															shouldValidate: false,
														});

														// borra errores y desregistra fields internos
														clearErrors('configuration.periods' as any);
														unregister('configuration.periods' as any);
														unregister('configuration.periods.*' as any);
													}

													// reconstruye config mínima
													const ts =
														(getValues('configuration.triggerSource' as any) as TriggerSource | undefined) ??
														TriggerSource.ServiceCreation;

													const built = buildEmptyConfig(next, ts);

													setValue('configuration' as any, built.configuration as any, {
														shouldDirty: true,
														shouldTouch: true,
														shouldValidate: true,
													});

													// si entro a CustomCalendar
													if (next === StrategyCode.CustomCalendar) {
														const currentPeriods =
															(getValues('configuration.periods' as any) as any[] | undefined) ?? [];
														periodsArray.replace(
															currentPeriods.length > 0
																? currentPeriods
																: [
																		{
																			from: null,
																			to: null,
																			dueDate: null,
																		},
																  ],
														);
														//setea el valor para que cargue los campos correspondientes a esa estrategia
														const fallbackStrategy = getValues('configuration.fallbackStrategy.strategyCode' as any);
														if (!fallbackStrategy) {
															setValue('configuration.fallbackStrategy.strategyCode' as any, StrategyCode.NetDays);
														} else {
															setValue('configuration.fallbackStrategy.strategyCode' as any, fallbackStrategy);
														}
													}

													clearErrors();
													trigger(); // recalcula isValid
												}}
											>
												{STRATEGY_OPTIONS.map((o) => (
													<MenuItem key={o.value} value={o.value}>
														{o.label}
													</MenuItem>
												))}
											</Select>
											<FormHelperText>{getErrorMessage(errors, 'strategyCode')}</FormHelperText>
										</FormControl>
									)}
								/>

								<Controller
									name={'configuration.triggerSource' as any}
									control={control}
									rules={{ required: 'Fecha base es requerida' }}
									render={({ field }) => (
										<FormControl fullWidth size="small" error={hasError(errors, 'configuration.triggerSource')}>
											<InputLabel>Fecha base (triggerSource)</InputLabel>
											<Select
												{...field}
												label="Fecha base (triggerSource)"
												value={field.value ?? TriggerSource.ServiceCreation}
												onChange={(e) => field.onChange(e.target.value as TriggerSource)}
											>
												{TRIGGER_OPTIONS.map((o) => (
													<MenuItem key={o.value} value={o.value}>
														{o.label}
													</MenuItem>
												))}
											</Select>
											<FormHelperText>{getErrorMessage(errors, 'configuration.triggerSource')}</FormHelperText>
										</FormControl>
									)}
								/>
							</Box>

							<Divider />

							{strategyCode === StrategyCode.NetDays && (
								<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
									<Controller
										name={'configuration.days' as any}
										control={control}
										rules={{
											required: 'Días es requerido',
											validate: (v) => {
												const ts = getValues('configuration.triggerSource' as any) as TriggerSource | undefined;

												// CheckIn/CheckOut => permite negativos (cualquier entero)
												if (allowNegativeNetDays(ts)) {
													return isIntAny(v) || 'Días debe ser un entero';
												}

												// ServiceCreation => solo >= 0
												return isIntGte0(v) || 'Días debe ser un entero >= 0';
											},
										}}
										render={({ field }) => (
											<TextField
												size="small"
												type="number"
												label="Días (NetDays)"
												fullWidth
												value={field.value ?? ''}
												onChange={(e) => {
													const raw = e.target.value;
													field.onChange(raw === '' ? '' : Number(raw));
												}}
												error={hasError(errors, 'configuration.days')}
												helperText={getErrorMessage(errors, 'configuration.days')}
											/>
										)}
									/>
								</Box>
							)}

							{strategyCode === StrategyCode.WeeklyRelative && (
								<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
									<Controller
										name={'configuration.weeksOffset' as any}
										control={control}
										rules={{
											required: 'Semanas de offset es requerido',
											validate: (v) => isIntGte0(v) || 'Semanas de offset debe ser un entero >= 0',
										}}
										render={({ field }) => (
											<TextField
												size="small"
												type="number"
												label="Semanas de offset (weeksOffset)"
												fullWidth
												value={field.value ?? ''}
												onChange={(e) => {
													const raw = e.target.value;
													field.onChange(raw === '' ? '' : Number(raw));
												}}
												error={hasError(errors, 'configuration.weeksOffset')}
												helperText={getErrorMessage(errors, 'configuration.weeksOffset')}
											/>
										)}
									/>

									<Controller
										name={'configuration.dayOfWeek' as any}
										control={control}
										rules={{ required: 'Día de semana es requerido' }}
										render={({ field }) => (
											<FormControl fullWidth size="small" error={hasError(errors, 'configuration.dayOfWeek')}>
												<InputLabel>Día de semana (dayOfWeek)</InputLabel>
												<Select
													label="Día de semana (dayOfWeek)"
													value={field.value ?? ''}
													onChange={(e) => field.onChange(e.target.value as DayOfWeekEnum)}
												>
													{DOW_OPTIONS.map((o) => (
														<MenuItem key={o.value} value={o.value}>
															{o.label}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>{getErrorMessage(errors, 'configuration.dayOfWeek')}</FormHelperText>
											</FormControl>
										)}
									/>
								</Box>
							)}

							{strategyCode === StrategyCode.MonthlyRelative && (
								<Box display="grid" gap={2} sx={{ gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
									<Controller
										name={'configuration.monthOffset' as any}
										control={control}
										rules={{
											required: 'Meses de offset es requerido',
											validate: (v) => isIntGte0(v) || 'Meses de offset debe ser un entero >= 0',
										}}
										render={({ field }) => (
											<TextField
												size="small"
												type="number"
												label="Meses de offset (monthOffset)"
												fullWidth
												value={field.value ?? ''}
												onChange={(e) => {
													const raw = e.target.value;
													field.onChange(raw === '' ? '' : Number(raw));
												}}
												error={hasError(errors, 'configuration.monthOffset')}
												helperText={getErrorMessage(errors, 'configuration.monthOffset')}
											/>
										)}
									/>

									<Controller
										name={'configuration.weekOrdinal' as any}
										control={control}
										rules={{
											required: 'Ordinal de semana es requerido',
											validate: (v) => {
												const n = Number(v);
												const ok = Number.isInteger(n) && (n === -1 || (n >= 1 && n <= 4));
												return ok || 'Ordinal debe ser -1 (último) o 1..4';
											},
										}}
										render={({ field }) => (
											<TextField
												size="small"
												type="number"
												label="Ordinal (weekOrdinal: -1 o 1..4)"
												fullWidth
												value={field.value ?? ''}
												onChange={(e) => {
													const raw = e.target.value;
													field.onChange(raw === '' ? '' : Number(raw));
												}}
												error={hasError(errors, 'configuration.weekOrdinal')}
												helperText={getErrorMessage(errors, 'configuration.weekOrdinal')}
											/>
										)}
									/>

									<Controller
										name={'configuration.dayOfWeek' as any}
										control={control}
										rules={{ required: 'Día de semana es requerido' }}
										render={({ field }) => (
											<FormControl fullWidth size="small" error={hasError(errors, 'configuration.dayOfWeek')}>
												<InputLabel>Día de semana (dayOfWeek)</InputLabel>
												<Select
													label="Día de semana (dayOfWeek)"
													value={field.value ?? ''}
													onChange={(e) => field.onChange(e.target.value as DayOfWeekEnum)}
												>
													{DOW_OPTIONS.map((o) => (
														<MenuItem key={o.value} value={o.value}>
															{o.label}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>{getErrorMessage(errors, 'configuration.dayOfWeek')}</FormHelperText>
											</FormControl>
										)}
									/>
								</Box>
							)}

							{strategyCode === StrategyCode.SplitMonth && (
								<Box display="flex" flexDirection="column" gap={2}>
									<Controller
										name={'configuration.cutoffDay' as any}
										control={control}
										rules={{
											required: 'Día de corte es requerido',
											validate: (v) => isIntBetween(v, 1, 31) || 'Día de corte debe estar entre 1 y 31',
										}}
										render={({ field }) => (
											<TextField
												size="small"
												type="number"
												label="Día de corte (cutoffDay: 1..31)"
												fullWidth
												value={field.value ?? ''}
												onChange={(e) => {
													const raw = e.target.value;
													field.onChange(raw === '' ? '' : Number(raw));
												}}
												error={hasError(errors, 'configuration.cutoffDay')}
												helperText={getErrorMessage(errors, 'configuration.cutoffDay')}
											/>
										)}
									/>

									<Typography variant="subtitle2">Primer período (firstPeriod)</Typography>
									<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
										<Controller
											name={'configuration.firstPeriod.monthOffset' as any}
											control={control}
											rules={{
												required: 'monthOffset es requerido',
												validate: (v) => isIntGte0(v) || 'monthOffset debe ser >= 0',
											}}
											render={({ field }) => (
												<TextField
													size="small"
													type="number"
													label="Meses offset (monthOffset)"
													fullWidth
													value={field.value ?? ''}
													onChange={(e) => {
														const raw = e.target.value;
														field.onChange(raw === '' ? '' : Number(raw));
													}}
													error={hasError(errors, 'configuration.firstPeriod.monthOffset')}
													helperText={getErrorMessage(errors, 'configuration.firstPeriod.monthOffset')}
												/>
											)}
										/>
										<Controller
											name={'configuration.firstPeriod.fixedDay' as any}
											control={control}
											rules={{
												required: 'fixedDay es requerido',
												validate: (v) => isIntBetween(v, 1, 31) || 'fixedDay debe estar entre 1 y 31',
											}}
											render={({ field }) => (
												<TextField
													size="small"
													type="number"
													label="Día fijo (fixedDay: 1..31)"
													fullWidth
													value={field.value ?? ''}
													onChange={(e) => {
														const raw = e.target.value;
														field.onChange(raw === '' ? '' : Number(raw));
													}}
													error={hasError(errors, 'configuration.firstPeriod.fixedDay')}
													helperText={getErrorMessage(errors, 'configuration.firstPeriod.fixedDay')}
												/>
											)}
										/>
									</Box>

									<Typography variant="subtitle2">Segundo período (secondPeriod)</Typography>
									<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
										<Controller
											name={'configuration.secondPeriod.monthOffset' as any}
											control={control}
											rules={{
												required: 'monthOffset es requerido',
												validate: (v) => isIntGte0(v) || 'monthOffset debe ser >= 0',
											}}
											render={({ field }) => (
												<TextField
													size="small"
													type="number"
													label="Meses offset (monthOffset)"
													fullWidth
													value={field.value ?? ''}
													onChange={(e) => {
														const raw = e.target.value;
														field.onChange(raw === '' ? '' : Number(raw));
													}}
													error={hasError(errors, 'configuration.secondPeriod.monthOffset')}
													helperText={getErrorMessage(errors, 'configuration.secondPeriod.monthOffset')}
												/>
											)}
										/>
										<Controller
											name={'configuration.secondPeriod.fixedDay' as any}
											control={control}
											rules={{
												required: 'fixedDay es requerido',
												validate: (v) => isIntBetween(v, 1, 31) || 'fixedDay debe estar entre 1 y 31',
											}}
											render={({ field }) => (
												<TextField
													size="small"
													type="number"
													label="Día fijo (fixedDay: 1..31)"
													fullWidth
													value={field.value ?? ''}
													onChange={(e) => {
														const raw = e.target.value;
														field.onChange(raw === '' ? '' : Number(raw));
													}}
													error={hasError(errors, 'configuration.secondPeriod.fixedDay')}
													helperText={getErrorMessage(errors, 'configuration.secondPeriod.fixedDay')}
												/>
											)}
										/>
									</Box>
								</Box>
							)}

							{strategyCode === StrategyCode.CustomCalendar && (
								<Box display="flex" flexDirection="column" gap={2}>
									<Box display="flex" alignItems="center" justifyContent="space-between">
										<Button
											startIcon={<AddIcon />}
											onClick={() => periodsArray.append({ from: null, to: null, dueDate: null } as any)}
											size="small"
											variant="outlined"
										>
											Agregar período
										</Button>
									</Box>
									<LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
										{periodsArray.fields.map((f, idx) => {
											const fromPath = `configuration.periods.${idx}.from`;
											const toPath = `configuration.periods.${idx}.to`;
											const duePath = `configuration.periods.${idx}.dueDate`;
											const fromYmd = getValues(fromPath as any) as string | undefined;
											return (
												<Box
													key={f.id}
													display="grid"
													gridTemplateColumns="1fr 1fr 1fr auto"
													gap={2}
													alignItems="center"
												>
													<Controller
														name={fromPath as any}
														control={control}
														rules={{ required: 'From es requerido' }}
														render={({ field }) => (
															<DatePicker
																label="Desde (from)"
																inputFormat="dd/MM/yyyy"
																value={ymdToLocalDate(field.value)}
																onChange={(d) => field.onChange(localDateToYmd(d))}
																renderInput={(params) => (
																	<TextField
																		{...params}
																		size="small"
																		fullWidth
																		error={hasError(errors, fromPath)}
																		helperText={getErrorMessage(errors, fromPath)}
																	/>
																)}
															/>
														)}
													/>

													<Controller
														name={toPath as any}
														control={control}
														rules={{
															required: 'To es requerido',
															validate: (value) => {
																const from = getValues(fromPath as any) as string | undefined;
																if (!from || !value) return true;
																return value >= from || 'To debe ser mayor o igual a From';
															},
														}}
														render={({ field }) => (
															<DatePicker
																label="Hasta (to)"
																inputFormat="dd/MM/yyyy"
																minDate={ymdToLocalDate(fromYmd ?? null)}
																value={ymdToLocalDate(field.value)}
																onChange={(d) => field.onChange(localDateToYmd(d))}
																renderInput={(params) => (
																	<TextField
																		{...params}
																		size="small"
																		fullWidth
																		error={hasError(errors, toPath)}
																		helperText={getErrorMessage(errors, toPath)}
																	/>
																)}
															/>
														)}
													/>

													<Controller
														name={duePath as any}
														control={control}
														rules={{
															required: 'Due date es requerido',
															validate: (value) => {
																const from = getValues(fromPath as any) as string | undefined;
																if (!from || !value) return true;
																return value >= from || 'Due date debe ser mayor o igual a From';
															},
														}}
														render={({ field }) => (
															<DatePicker
																label="Vencimiento (dueDate)"
																inputFormat="dd/MM/yyyy"
																value={ymdToLocalDate(field.value)}
																minDate={ymdToLocalDate(fromYmd ?? null)}
																onChange={(d) => field.onChange(localDateToYmd(d))}
																renderInput={(params) => (
																	<TextField
																		{...params}
																		size="small"
																		fullWidth
																		error={hasError(errors, duePath)}
																		helperText={getErrorMessage(errors, duePath)}
																	/>
																)}
															/>
														)}
													/>

													<IconButton
														onClick={() => {
															if (periodsArray.fields.length <= 1) return;
															periodsArray.remove(idx);
														}}
														disabled={periodsArray.fields.length <= 1}
														size="small"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Box>
											);
										})}
									</LocalizationProvider>

									<Divider />

									<Typography variant="subtitle2">Estrategia de fallback (fallbackStrategy)</Typography>

									<Controller
										name={'configuration.fallbackStrategy.strategyCode' as any}
										control={control}
										rules={{
											required: 'Fallback es requerido',
											validate: (v) => v !== StrategyCode.CustomCalendar || 'CustomCalendar no puede ser fallback',
										}}
										render={({ field }) => (
											<FormControl
												fullWidth
												size="small"
												error={hasError(errors, 'configuration.fallbackStrategy.strategyCode')}
											>
												<InputLabel>Fallback</InputLabel>
												<Select
													{...field}
													label="Fallback"
													value={field.value ?? ''}
													onChange={(e) => {
														const next = e.target.value as StrategyCode;
														field.onChange(next);

														// SOLO estructura mínima, sin defaults
														setValue('configuration.fallbackStrategy' as any, { strategyCode: next } as any, {
															shouldDirty: true,
															shouldTouch: true,
															shouldValidate: true,
														});
													}}
												>
													{[
														StrategyCode.NetDays,
														StrategyCode.MonthlyRelative,
														StrategyCode.WeeklyRelative,
														StrategyCode.SplitMonth,
													].map((v) => (
														<MenuItem key={v} value={v}>
															{STRATEGY_LABEL[v]}
														</MenuItem>
													))}
												</Select>
												<FormHelperText>
													{getErrorMessage(errors, 'configuration.fallbackStrategy.strategyCode')}
												</FormHelperText>
											</FormControl>
										)}
									/>

									<FallbackFields
										control={control}
										errors={errors}
										fbCode={fallbackStrategyCode}
										dayOfWeekOptions={DOW_OPTIONS}
									/>
								</Box>
							)}
						</Box>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose} disabled={disableClose} size="small">
						Cancelar
					</Button>
					<Button
						onClick={handleSubmit(onSubmit)}
						variant="contained"
						disabled={saving || loading || !isValid}
						size="small"
					>
						{saving ? 'Guardando...' : 'Guardar'}
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar open={alert.show} autoHideDuration={2000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};

type FallbackFieldsProps = {
	control: Control<ProviderPaymentDateRuleUpsertDTO>;
	errors: FieldErrors<ProviderPaymentDateRuleUpsertDTO>;
	fbCode?: StrategyCode;
	dayOfWeekOptions: Option<DayOfWeekEnum>[];
};

const FallbackFields: React.FC<FallbackFieldsProps> = ({
	control,
	errors,
	fbCode,
	dayOfWeekOptions,
}: FallbackFieldsProps) => {
	const triggerSource = useWatch({
		control,
		name: 'configuration.triggerSource' as any,
	}) as TriggerSource | undefined;
	const allowNegative = triggerSource === TriggerSource.CheckIn || triggerSource === TriggerSource.CheckOut;

	if (!fbCode) return null;

	if (fbCode === StrategyCode.NetDays) {
		return (
			<Controller
				name={'configuration.fallbackStrategy.days' as any}
				control={control}
				rules={{
					required: 'Días es requerido',
					validate: (v) => {
						if (v === '' || v === null || v === undefined) return 'Días es requerido';
						const n = Number(v);
						if (!Number.isInteger(n)) return 'Días debe ser un entero';
						if (!allowNegative && n < 0) return 'Días debe ser un entero >= 0';
						return true;
					},
				}}
				render={({ field }) => (
					<TextField
						size="small"
						type="number"
						label="Días (NetDays)"
						fullWidth
						value={field.value ?? ''}
						onChange={(e) => {
							const raw = e.target.value;
							field.onChange(raw === '' ? '' : Number(raw));
						}}
						error={hasError(errors, 'configuration.fallbackStrategy.days')}
						helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.days')}
					/>
				)}
			/>
		);
	}

	if (fbCode === StrategyCode.WeeklyRelative) {
		return (
			<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
				<Controller
					name={'configuration.fallbackStrategy.weeksOffset' as any}
					control={control}
					rules={{
						required: 'weeksOffset es requerido',
						validate: (v) => isIntGte0(v) || 'weeksOffset debe ser >= 0',
					}}
					render={({ field }) => (
						<TextField
							size="small"
							type="number"
							label="Semanas de offset (weeksOffset)"
							fullWidth
							value={field.value ?? ''}
							onChange={(e) => {
								const raw = e.target.value;
								field.onChange(raw === '' ? '' : Number(raw));
							}}
							error={hasError(errors, 'configuration.fallbackStrategy.weeksOffset')}
							helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.weeksOffset')}
						/>
					)}
				/>

				<Controller
					name={'configuration.fallbackStrategy.dayOfWeek' as any}
					control={control}
					rules={{ required: 'dayOfWeek es requerido' }}
					render={({ field }) => (
						<FormControl fullWidth size="small" error={hasError(errors, 'configuration.fallbackStrategy.dayOfWeek')}>
							<InputLabel>Día de semana (dayOfWeek)</InputLabel>
							<Select
								label="Día de semana (dayOfWeek)"
								value={field.value ?? ''}
								onChange={(e) => field.onChange(e.target.value as DayOfWeekEnum)}
							>
								{dayOfWeekOptions.map((o) => (
									<MenuItem key={o.value} value={o.value}>
										{o.label}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>{getErrorMessage(errors, 'configuration.fallbackStrategy.dayOfWeek')}</FormHelperText>
						</FormControl>
					)}
				/>
			</Box>
		);
	}

	if (fbCode === StrategyCode.MonthlyRelative) {
		return (
			<Box display="grid" gap={2} sx={{ gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
				<Controller
					name={'configuration.fallbackStrategy.monthOffset' as any}
					control={control}
					rules={{
						required: 'monthOffset es requerido',
						validate: (v) => isIntGte0(v) || 'monthOffset debe ser >= 0',
					}}
					render={({ field }) => (
						<TextField
							size="small"
							type="number"
							label="Meses de offset (monthOffset)"
							fullWidth
							value={field.value ?? ''}
							onChange={(e) => {
								const raw = e.target.value;
								field.onChange(raw === '' ? '' : Number(raw));
							}}
							error={hasError(errors, 'configuration.fallbackStrategy.monthOffset')}
							helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.monthOffset')}
						/>
					)}
				/>

				<Controller
					name={'configuration.fallbackStrategy.weekOrdinal' as any}
					control={control}
					rules={{
						required: 'weekOrdinal es requerido',
						validate: (v) => {
							const n = Number(v);
							const ok = Number.isInteger(n) && (n === -1 || (n >= 1 && n <= 5));
							return ok || 'weekOrdinal debe ser -1 o 1..5';
						},
					}}
					render={({ field }) => (
						<TextField
							size="small"
							type="number"
							label="Ordinal (weekOrdinal: -1 o 1..5)"
							fullWidth
							value={field.value ?? ''}
							onChange={(e) => {
								const raw = e.target.value;
								field.onChange(raw === '' ? '' : Number(raw));
							}}
							error={hasError(errors, 'configuration.fallbackStrategy.weekOrdinal')}
							helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.weekOrdinal')}
						/>
					)}
				/>

				<Controller
					name={'configuration.fallbackStrategy.dayOfWeek' as any}
					control={control}
					rules={{ required: 'dayOfWeek es requerido' }}
					render={({ field }) => (
						<FormControl fullWidth size="small" error={hasError(errors, 'configuration.fallbackStrategy.dayOfWeek')}>
							<InputLabel>Día de semana (dayOfWeek)</InputLabel>
							<Select
								label="Día de semana (dayOfWeek)"
								value={field.value ?? ''}
								onChange={(e) => field.onChange(e.target.value as DayOfWeekEnum)}
							>
								{dayOfWeekOptions.map((o) => (
									<MenuItem key={o.value} value={o.value}>
										{o.label}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>{getErrorMessage(errors, 'configuration.fallbackStrategy.dayOfWeek')}</FormHelperText>
						</FormControl>
					)}
				/>
			</Box>
		);
	}

	if (fbCode === StrategyCode.SplitMonth) {
		return (
			<Box display="flex" flexDirection="column" gap={2}>
				<Controller
					name={'configuration.fallbackStrategy.cutoffDay' as any}
					control={control}
					rules={{
						required: 'cutoffDay es requerido',
						validate: (v) => isIntBetween(v, 1, 31) || 'cutoffDay debe estar entre 1 y 31',
					}}
					render={({ field }) => (
						<TextField
							size="small"
							type="number"
							label="Día de corte (cutoffDay: 1..31)"
							fullWidth
							value={field.value ?? ''}
							onChange={(e) => {
								const raw = e.target.value;
								field.onChange(raw === '' ? '' : Number(raw));
							}}
							error={hasError(errors, 'configuration.fallbackStrategy.cutoffDay')}
							helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.cutoffDay')}
						/>
					)}
				/>

				<Typography variant="subtitle2">Primer período (firstPeriod)</Typography>
				<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
					<Controller
						name={'configuration.fallbackStrategy.firstPeriod.monthOffset' as any}
						control={control}
						rules={{
							required: 'monthOffset es requerido',
							validate: (v) => isIntGte0(v) || 'monthOffset debe ser >= 0',
						}}
						render={({ field }) => (
							<TextField
								size="small"
								type="number"
								label="Meses offset (monthOffset)"
								fullWidth
								value={field.value ?? ''}
								onChange={(e) => {
									const raw = e.target.value;
									field.onChange(raw === '' ? '' : Number(raw));
								}}
								error={hasError(errors, 'configuration.fallbackStrategy.firstPeriod.monthOffset')}
								helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.firstPeriod.monthOffset')}
							/>
						)}
					/>
					<Controller
						name={'configuration.fallbackStrategy.firstPeriod.fixedDay' as any}
						control={control}
						rules={{
							required: 'fixedDay es requerido',
							validate: (v) => isIntBetween(v, 1, 31) || 'fixedDay debe estar entre 1 y 31',
						}}
						render={({ field }) => (
							<TextField
								size="small"
								type="number"
								label="Día fijo (fixedDay: 1..31)"
								fullWidth
								value={field.value ?? ''}
								onChange={(e) => {
									const raw = e.target.value;
									field.onChange(raw === '' ? '' : Number(raw));
								}}
								error={hasError(errors, 'configuration.fallbackStrategy.firstPeriod.fixedDay')}
								helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.firstPeriod.fixedDay')}
							/>
						)}
					/>
				</Box>

				<Typography variant="subtitle2">Segundo período (secondPeriod)</Typography>
				<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
					<Controller
						name={'configuration.fallbackStrategy.secondPeriod.monthOffset' as any}
						control={control}
						rules={{
							required: 'monthOffset es requerido',
							validate: (v) => isIntGte0(v) || 'monthOffset debe ser >= 0',
						}}
						render={({ field }) => (
							<TextField
								size="small"
								type="number"
								label="Meses offset (monthOffset)"
								fullWidth
								value={field.value ?? ''}
								onChange={(e) => {
									const raw = e.target.value;
									field.onChange(raw === '' ? '' : Number(raw));
								}}
								error={hasError(errors, 'configuration.fallbackStrategy.secondPeriod.monthOffset')}
								helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.secondPeriod.monthOffset')}
							/>
						)}
					/>
					<Controller
						name={'configuration.fallbackStrategy.secondPeriod.fixedDay' as any}
						control={control}
						rules={{
							required: 'fixedDay es requerido',
							validate: (v) => isIntBetween(v, 1, 31) || 'fixedDay debe estar entre 1 y 31',
						}}
						render={({ field }) => (
							<TextField
								size="small"
								type="number"
								label="Día fijo (fixedDay: 1..31)"
								fullWidth
								value={field.value ?? ''}
								onChange={(e) => {
									const raw = e.target.value;
									field.onChange(raw === '' ? '' : Number(raw));
								}}
								error={hasError(errors, 'configuration.fallbackStrategy.secondPeriod.fixedDay')}
								helperText={getErrorMessage(errors, 'configuration.fallbackStrategy.secondPeriod.fixedDay')}
							/>
						)}
					/>
				</Box>
			</Box>
		);
	}

	return null;
};

export default ProviderPaymentDateRuleModal;
